import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useEffect, useCallback } from "react";
import { format } from "date-fns";
import { api } from "../lib/api";
import {
  Button,
  Card,
  CardContent,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  Slider,
  Textarea,
  useToast,
} from "@wondermaps/ui";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/pro-solid-svg-icons/faFileExport";
import { faChartMixed } from "@fortawesome/pro-solid-svg-icons/faChartMixed";
import { DateRangeSelector } from "../components/engagement-report/DateRangeSelector";
import { ReportDashboard } from "../components/engagement-report/ReportDashboard";
import { SuperadminHeader } from "../components/superadmin/SuperadminHeader";
import type { EngagementReportData } from "../components/engagement-report/types";
import type { SuperadminOrg } from "../types/superadmin";

export const Route = createFileRoute("/superadmin/engagement-reports")({
  component: EngagementReportsPage,
});

function daysAgo(n: number): Date {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d;
}

const STORAGE_KEY_PREFIX = "wm-engagement-report-";

function EngagementReportsPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Org selection
  const [selectedOrgSlug, setSelectedOrgSlug] = useState<string>("");

  // Date range
  const [startDate, setStartDate] = useState<Date>(daysAgo(90));
  const [endDate, setEndDate] = useState<Date>(new Date());

  // Dead zone threshold
  const [deadZoneThreshold, setDeadZoneThreshold] = useState(5);

  // Venue context (localStorage persisted)
  const [totalVenueVisitors, setTotalVenueVisitors] = useState("");
  const [operatingDays, setOperatingDays] = useState("");
  const [reportNotes, setReportNotes] = useState("");
  const [hasDirtyFields, setHasDirtyFields] = useState(false);

  // Load venue context from localStorage when org changes
  useEffect(() => {
    if (!selectedOrgSlug) return;
    const key = STORAGE_KEY_PREFIX + selectedOrgSlug;
    try {
      const saved = JSON.parse(localStorage.getItem(key) || "{}");
      setTotalVenueVisitors(saved.totalVenueVisitors ?? "");
      setOperatingDays(saved.operatingDays ?? "");
      setReportNotes(saved.reportNotes ?? "");
    } catch {
      // ignore parse errors
    }
    setHasDirtyFields(false);
  }, [selectedOrgSlug]);

  // Save to localStorage
  const saveContext = useCallback(() => {
    if (!selectedOrgSlug) return;
    const key = STORAGE_KEY_PREFIX + selectedOrgSlug;
    localStorage.setItem(
      key,
      JSON.stringify({ totalVenueVisitors, operatingDays, reportNotes })
    );
    setHasDirtyFields(false);
  }, [selectedOrgSlug, totalVenueVisitors, operatingDays, reportNotes]);

  // Warn on unload with dirty fields
  useEffect(() => {
    if (!hasDirtyFields) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirtyFields]);

  // Fetch orgs
  const { data: orgsData, isLoading: isOrgsLoading } = useQuery({
    queryKey: ["superadmin", "orgs"],
    queryFn: async () => {
      const res = await api.orgs.admin.$get();
      if (!res.ok) throw new Error("Failed to fetch organizations");
      return res.json() as Promise<{ orgs: SuperadminOrg[] }>;
    },
  });

  // Generate trigger — manual fetch on click
  const [generateEnabled, setGenerateEnabled] = useState(false);

  // Clear stale report when params change
  const invalidateReport = useCallback(() => {
    setGenerateEnabled(false);
    queryClient.removeQueries({ queryKey: ["engagement-report"] });
  }, [queryClient]);

  const {
    data: reportData,
    isLoading: isReportLoading,
    error: reportError,
  } = useQuery({
    queryKey: [
      "engagement-report",
      selectedOrgSlug,
      format(startDate, "yyyy-MM-dd"),
      format(endDate, "yyyy-MM-dd"),
      deadZoneThreshold,
    ],
    queryFn: async () => {
      const res = await api["engagement-reports"][":orgSlug"].$get({
        param: { orgSlug: selectedOrgSlug },
        query: {
          start: format(startDate, "yyyy-MM-dd"),
          end: format(endDate, "yyyy-MM-dd"),
          deadZoneThreshold: String(deadZoneThreshold),
        },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          (err as { error?: string }).error ?? "Report generation failed"
        );
      }
      return res.json() as Promise<EngagementReportData>;
    },
    enabled: generateEnabled && !!selectedOrgSlug,
    retry: false,
  });

  const handleGenerate = () => {
    if (!selectedOrgSlug) {
      toast({
        title: "Select an organization",
        variant: "destructive",
      });
      return;
    }
    setGenerateEnabled(true);
  };

  const handleExportPDF = async () => {
    if (!reportData) return;
    const selectedOrg = orgsData?.orgs.find(
      (o) => o.slug === selectedOrgSlug
    );
    try {
      const { generateEngagementPDF } = await import(
        "../components/engagement-report/pdf/generate"
      );
      await generateEngagementPDF({
        data: reportData,
        orgName: selectedOrg?.name ?? selectedOrgSlug,
        startDate: format(startDate, "MMM d, yyyy"),
        endDate: format(endDate, "MMM d, yyyy"),
        reportNotes,
        venueContext: {
          totalVenueVisitors: totalVenueVisitors
            ? Number(totalVenueVisitors)
            : undefined,
          operatingDays: operatingDays ? Number(operatingDays) : undefined,
        },
      });
      toast({ title: "PDF exported" });
    } catch (err) {
      toast({
        title: "PDF export failed",
        description: err instanceof Error ? err.message : "Unknown error",
        variant: "destructive",
      });
    }
  };

  const orgs = orgsData?.orgs ?? [];

  return (
    <div className="flex h-screen flex-col bg-background">
      <SuperadminHeader
        title="Seasonal Engagement Reports"
        subtitle="Generate curated analytics reports for client orgs"
        actions={
          reportData ? (
            <Button onClick={handleExportPDF}>
              <FontAwesomeIcon
                icon={faFileExport}
                className="h-4 w-4 mr-2"
              />
              Export PDF
            </Button>
          ) : undefined
        }
      />

      {/* Content */}
      <main className="flex-1 overflow-auto p-6">
        <div className="container mx-auto max-w-5xl space-y-6">
          {/* Controls */}
          <Card>
            <CardContent className="p-6 space-y-4">
              <div className="flex flex-wrap gap-4 items-end">
                {/* Org selector */}
                <div className="space-y-1">
                  <Label>Organization</Label>
                  {isOrgsLoading ? (
                    <Skeleton className="h-10 w-[200px]" />
                  ) : (
                    <Select
                      value={selectedOrgSlug}
                      onValueChange={(v) => {
                        setSelectedOrgSlug(v);
                        invalidateReport();
                      }}
                    >
                      <SelectTrigger className="w-[200px]">
                        <SelectValue placeholder="Select org..." />
                      </SelectTrigger>
                      <SelectContent>
                        {orgs.map((org) => (
                          <SelectItem key={org.slug} value={org.slug}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* Date range */}
                <div className="space-y-1">
                  <Label>Date Range</Label>
                  <DateRangeSelector
                    start={startDate}
                    end={endDate}
                    onChangeStart={(d) => {
                      setStartDate(d);
                      invalidateReport();
                    }}
                    onChangeEnd={(d) => {
                      setEndDate(d);
                      invalidateReport();
                    }}
                  />
                </div>
              </div>

              {/* Dead zone threshold */}
              <div className="flex items-center gap-4">
                <Label className="whitespace-nowrap">
                  Dead zone threshold: {deadZoneThreshold}
                </Label>
                <Slider
                  value={[deadZoneThreshold]}
                  onValueChange={(v) => {
                    setDeadZoneThreshold(v[0]);
                    invalidateReport();
                  }}
                  min={0}
                  max={50}
                  step={1}
                  className="w-48"
                />
              </div>

              {/* Venue context */}
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-1">
                  <Label>Total Venue Visitors (optional)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 50000"
                    value={totalVenueVisitors}
                    onChange={(e) => {
                      setTotalVenueVisitors(e.target.value);
                      setHasDirtyFields(true);
                    }}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Operating Days (optional)</Label>
                  <Input
                    type="number"
                    placeholder="e.g. 90"
                    value={operatingDays}
                    onChange={(e) => {
                      setOperatingDays(e.target.value);
                      setHasDirtyFields(true);
                    }}
                  />
                </div>
                <div className="flex items-end">
                  {hasDirtyFields && (
                    <Button variant="outline" size="sm" onClick={saveContext}>
                      Save Context
                    </Button>
                  )}
                </div>
              </div>

              {/* Report notes */}
              <div className="space-y-1">
                <Label>Report Notes (included on PDF cover page)</Label>
                <Textarea
                  placeholder="Add notes about the season, special events, or context for the board..."
                  value={reportNotes}
                  onChange={(e) => {
                    setReportNotes(e.target.value);
                    setHasDirtyFields(true);
                  }}
                  rows={3}
                />
              </div>

              {/* Generate button */}
              <Button
                onClick={handleGenerate}
                disabled={!selectedOrgSlug || isReportLoading}
                className="w-full sm:w-auto"
              >
                <FontAwesomeIcon
                  icon={faChartMixed}
                  className="h-4 w-4 mr-2"
                />
                {isReportLoading ? "Generating..." : "Generate Report"}
              </Button>
            </CardContent>
          </Card>

          {/* Report Dashboard */}
          <ReportDashboard
            data={reportData}
            isLoading={isReportLoading}
            error={reportError}
            deadZoneThreshold={deadZoneThreshold}
            venueContext={{
              totalVenueVisitors: totalVenueVisitors
                ? Number(totalVenueVisitors)
                : undefined,
              operatingDays: operatingDays
                ? Number(operatingDays)
                : undefined,
            }}
          />
        </div>
      </main>
    </div>
  );
}
